import flagsmith from '@flagsmith/flagsmith';
import {
  FlagsmithProvider,
  useFlags,
  useFlagsmith,
  useFlagsmithLoading,
} from '@flagsmith/flagsmith/react';
import { useEffect, useEffectEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { getClientProfileId } from '@/config/axios';
import { ADMIN_ROLE, CLIENT_ROLE, RECRUITER_ROLE } from '@/constants/roles';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import useHirePortContext from '@/hooks/useHirePortContext';
import useLocale from '@/hooks/useLocale';
import { getAccessToken, getAccessTokenType } from '@/utils/localStorage';
import { isRecord } from '@/utils/typeGuards';

import type { FlagsmithContextType } from '@flagsmith/flagsmith/react';
import type { ReactNode } from 'react';

export const TEST_UI_KEYS = {
  SelectTalentSuppliers: 'test_ui_select_talent_suppliers',
  MasterContracts: 'test_ui_master_contracts',
  RecruiterManagement: 'test_ui_recruiter_management',
} as const;

const getFlagsmithApiKey = (): string | undefined =>
  import.meta.env.VITE_FLAGSMITH_CLIENT_SDK_KEY;

/** One init per JS realm — avoids re-init on every Redux user object change. */
let flagsmithInitPromise: Promise<void> | null = null;

type HirePortIFrameMessage = {
  type: 'HirePortIFrameMessage';
  [key: string]: unknown;
};

type IframeTestConfig = {
  type: 'iframe';
  args: {
    url: string;
    width?: number | string;
    height?: number | string;
  };
};

function isIframeTestConfig(value: unknown): value is IframeTestConfig {
  return (
    isRecord(value) &&
    value.type === 'iframe' &&
    isRecord(value.args) &&
    typeof value.args.url === 'string'
  );
}

type FlagsmithClient = {
  init: (options: { environmentID: string }) => Promise<unknown>;
  identify: (
    id: string,
    traits?: Record<string, string | number | boolean | null>,
  ) => Promise<unknown>;
};

async function ensureFlagsmithInit(
  client: FlagsmithClient,
  apiKey: string,
): Promise<void> {
  if (!flagsmithInitPromise) {
    flagsmithInitPromise = Promise.resolve(
      client.init({ environmentID: apiKey }),
    ).then(() => undefined);
  }
  await flagsmithInitPromise;
}

const FlagsmithIdentifier = ({
  children,
}: {
  children: FlagsmithContextType['children'];
}) => {
  const { user } = useCurrentUser();
  const flagsmithClient = useFlagsmith() as FlagsmithClient;

  const userId = user?.id;
  const email = user?.email ?? null;
  const name = user?.name ?? null;
  const companyName = user?.company?.name ?? null;
  const companyId = user?.company?.id ?? null;
  const role = user?.role;

  const identifyFlagsmithUser = useEffectEvent(() => {
    const apiKey = getFlagsmithApiKey();
    if (!userId || !apiKey) return;

    const client = flagsmithClient;
    void (async () => {
      await ensureFlagsmithInit(client, apiKey);
      await client.identify(userId, {
        email,
        name,
        companyName,
        companyId,
        isClient: role === CLIENT_ROLE,
        isRecruiter: role === RECRUITER_ROLE,
        isAdmin: role === ADMIN_ROLE,
      });
    })();
  });

  useEffect(() => {
    identifyFlagsmithUser();
  }, [userId, email, name, companyName, companyId, role]);

  return <>{children}</>;
};

export const TestUIProvider = ({
  children,
}: {
  children: FlagsmithContextType['children'];
}) => (
  <FlagsmithProvider flagsmith={flagsmith}>
    <FlagsmithIdentifier>{children}</FlagsmithIdentifier>
  </FlagsmithProvider>
);

const tryParse = (str: unknown): unknown => {
  if (str == null || str === '') {
    return null;
  }
  if (typeof str === 'string') {
    try {
      return JSON.parse(str) as unknown;
    } catch {
      return null;
    }
  }
  if (typeof str === 'number' || typeof str === 'boolean') {
    try {
      return JSON.parse(String(str)) as unknown;
    } catch {
      return null;
    }
  }
  return null;
};

export type TestUIWrapperProps = {
  testKey: string;
  params?: Record<string, string | number | boolean | null | undefined>;
  defaultWidth?: number | string;
  defaultHeight?: number | string;
  sendToken?: boolean;
  children: ReactNode;
  sendUrlEncodedUrl?: boolean;
  sendBase64EncodedUrl?: boolean;
};

export function TestUIWrapper({
  testKey,
  params,
  defaultWidth = 800,
  defaultHeight = 600,
  sendToken = true,
  sendUrlEncodedUrl = false,
  sendBase64EncodedUrl = true,
  children,
}: TestUIWrapperProps) {
  const messageListener = useRef<((ev: MessageEvent) => void) | null>(null);
  const { sessionLocale: lang } = useLocale();
  const { t } = useTranslation();
  const { theme } = useHirePortContext();

  useEffect(() => {
    if (messageListener.current) return;
    const callback = (ev: MessageEvent<HirePortIFrameMessage>) => {
      if (ev.data?.type === 'HirePortIFrameMessage')
        window.postMessage(ev.data, '*');
    };
    messageListener.current = callback;
    window.addEventListener('message', callback);
    return () => {
      if (messageListener.current)
        window.removeEventListener('message', messageListener.current);
    };
  }, [messageListener]);

  const flags = useFlags([testKey]);
  const loadingState = useFlagsmithLoading();
  const flagsmithApiKey = getFlagsmithApiKey();

  // Flagsmith iframe test UI is for manual QA only — it hides real UI while loading and breaks Cypress.
  if (typeof globalThis !== 'undefined' && 'Cypress' in globalThis) {
    return <>{children}</>;
  }

  // Hide only on the first Flagsmith resolve. Background isFetching (e.g. re-identify)
  // must not unmount product UI — that caused ATS Agency Portal flicker.
  if (flagsmithApiKey && (!loadingState || loadingState.isLoading)) {
    return null;
  }
  if (!flagsmithApiKey || loadingState?.error || !flags[testKey].enabled)
    return <>{children}</>;

  const config = tryParse(flags[testKey].value);
  if (!isIframeTestConfig(config)) return <>{children}</>;

  const token = sendToken ? getAccessToken() : null;
  const token_type = sendToken ? getAccessTokenType() : null;
  const client_profile = sendToken ? getClientProfileId() : null;
  const location = window.location.toString();
  const url = `${config.args.url}${config.args.url.includes('?') ? '&' : '?'}${new URLSearchParams(
    {
      themeName: theme.themeName,
      lang,
      ...(token ? { token } : null),
      ...(token_type ? { token_type } : null),
      ...(client_profile ? { client_profile } : null),
      ...(sendUrlEncodedUrl && location ? { url: location } : null),
      ...(sendBase64EncodedUrl && location ? { b64url: btoa(location) } : null),
      ...params,
    },
  ).toString()}`;
  return (
    <div>
      <iframe
        title={t('AGENCY_PORTAL')}
        style={{
          width: config.args.width || defaultWidth,
          height: config.args.height || defaultHeight,
          padding: 0,
          border: 0,
        }}
        src={url}
      ></iframe>
    </div>
  );
}
